/** Browser interaction guards for game chrome.
 *
 * The game UI is an interaction surface, not a document. Prevent native text
 * selection, image dragging, and browser context menus everywhere except
 * controls where text editing is the intended interaction. */

const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const control = target.closest("input, textarea, select, [contenteditable]");
  if (!control || control.getAttribute("contenteditable") === "false") return false;
  if (control instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(control.type);
  return true;
}

/** Install once on the app root; descendants may still opt into text entry. */
export function installInputGuards(root: HTMLElement): void {
  const clearSelection = (): void => {
    window.getSelection()?.removeAllRanges();
  };
  const suppress = (ev: Event): void => {
    if (isTextEntry(ev.target)) return;
    ev.preventDefault();
    clearSelection();
  };

  // Capture at the app boundary so every DOM surface gets the same rule while
  // the event still reaches custom handlers (equipment/context-menu actions).
  root.addEventListener("selectstart", suppress, true);
  root.addEventListener("dragstart", suppress, true);
  root.addEventListener("contextmenu", suppress, true);
  root.addEventListener("auxclick", suppress, true);
  root.addEventListener("pointerdown", (ev: PointerEvent) => {
    if (isTextEntry(ev.target)) return;
    clearSelection();
    // The later contextmenu/auxclick events are too late to stop a browser
    // selection or drag that began on this press.
    if (ev.button !== 0) ev.preventDefault();
  }, true);
}
